
import { Component, Vue } from "vue-property-decorator";
import UIkit from "uikit";
import FabricService from "@/services/fabric_service";
import BrandFields from "@/components/BrandFields.vue";
import FabricNameFields from "@/components/FabricNameFields.vue";
import FabricComparisonPicker from "@/components/FabricComparisonPicker.vue";
import FabricImageUpload from "@/components/FabricImageUpload.vue";
import {
  IFabric,
  emptyFabric,
  missingRequiredFields,
  missingFieldKeys,
  COLOR_GROUPS,
  COLOR_HEX,
  PATTERN_GROUPS,
  GRADES,
  FABRIC_TYPES,
  WEIGHT_OPTIONS,
} from "@/models/fabric";
import FabricStatusPicker from "@/components/FabricStatusPicker.vue";

@Component({
  components: {
    BrandFields,
    FabricNameFields,
    FabricComparisonPicker,
    FabricImageUpload,
    FabricStatusPicker,
  },
})
export default class AddView extends Vue {
  private fabricService = new FabricService();
  protected fabric: IFabric = emptyFabric();
  protected saving = false;

  /**
   * The _id of the fabric once the API has minted one. Empty until then, which is
   * exactly what the upload blocks key off: with no id they stage but cannot
   * upload, which is the whole reason the metadata POST has to go first.
   */
  protected createdId = "";

  protected assetsPending: { swatch: boolean; texture: boolean } = {
    swatch: false,
    texture: false,
  };

  $refs!: {
    swatchUpload: FabricImageUpload;
    textureUpload: FabricImageUpload;
  };

  protected colorGroupModel: string[] = [];
  protected patternGroupModel: string[] = [];

  // Full catalog, for the comparison pickers (id resolution + search). Same cached
  // fetch the brand/mfr dropdowns below already make.
  protected allFabrics: IFabric[] = [];

  protected brandOptions: string[] = [];
  protected mfrOptions: string[] = [];
  protected collectionOptions: string[] = [];

  protected colorGroups = COLOR_GROUPS;
  protected patternGroups = PATTERN_GROUPS;
  protected grades = GRADES;
  protected fabricTypes = FABRIC_TYPES;

  // Set when this form was reached via "Duplicate" from an existing fabric, so
  // the template can tell the user what they're cloning (and that the image
  // fields were intentionally left empty).
  protected duplicatedFrom = "";

  async created(): Promise<void> {
    this.applyDraft();
    try {
      const all = await this.fabricService.list();
      this.allFabrics = all;
      const brands = new Set<string>();
      const mfrs = new Set<string>();
      const collections = new Set<string>();
      for (const f of all) {
        if (f.brand?.name) brands.add(f.brand.name);
        if (f.brand?.mfr?.name) mfrs.add(f.brand.mfr.name);
        if (f.brand?.brand_collection?.name) collections.add(f.brand.brand_collection.name);
      }
      this.brandOptions = Array.from(brands).sort();
      this.mfrOptions = Array.from(mfrs).sort();
      this.collectionOptions = Array.from(collections).sort();
    } catch (e) {
      // Non-fatal.
    }
  }

  /**
   * Seed the blank form from a "Duplicate" handoff, if one is pending.
   *
   * Merged over emptyFabric() rather than used as-is, so any field the clone
   * deliberately omitted (mfr_code, names, image, texture_key) keeps its blank
   * default instead of arriving as `undefined` and breaking a v-model binding.
   *
   * Consume-once: the draft is cleared immediately on read, so a later, unrelated
   * visit to /new starts blank rather than resurrecting a clone the user
   * abandoned. This is also why it survives the round trip at all — the draft
   * lives in the store, not the URL.
   */
  private applyDraft(): void {
    const draft = this.$store.getters.fabricDraft as Partial<IFabric> | null;
    if (!draft) return;
    this.$store.commit("CLEAR_FABRIC_DRAFT");

    const base = emptyFabric();
    this.fabric = { ...base, ...draft, name: { ...base.name, ...(draft.name || {}) } } as IFabric;
    this.colorGroupModel = [...(draft.color_group || [])];
    this.patternGroupModel = [...(draft.pattern_group || [])];
    this.duplicatedFrom = (this.$route.query.from as string) || "";
  }

  protected colorHex(color: string): string {
    return COLOR_HEX[color] || "#cccccc";
  }

  protected get weightOptions(): string[] {
    const opts = [...WEIGHT_OPTIONS];
    const w = this.fabric.weight;
    if (w && !opts.includes(w)) opts.unshift(w);
    return opts;
  }

  protected weightLabel(w: string): string {
    return WEIGHT_OPTIONS.includes(w) ? `${w} oz` : w;
  }

  protected get missingFieldLabels(): string[] {
    return missingRequiredFields(this.fabric).map((m) => m.label);
  }

  /** Dotted keys of the missing fields — the forms paint these inputs red. */
  protected get invalidFields(): Record<string, boolean> {
    return missingFieldKeys(this.fabric);
  }

  protected get isValid(): boolean {
    return this.missingFieldLabels.length === 0;
  }

  /**
   * Refuse the save out loud.
   *
   * The disabled button and the red fields are both easy to miss: the button may
   * be off-screen, and the offending field is wherever it happens to be on a long
   * form. A submit attempt is a deliberate act, so it gets a deliberate answer.
   */
  protected notifyInvalid(): void {
    UIkit.notification({
      message: `Can’t save yet — required: ${this.missingFieldLabels.join(", ")}.`,
      status: "danger",
      pos: "top-center",
      timeout: 6000,
    });
  }

  protected get pendingAssetCount(): number {
    return Number(this.assetsPending.swatch) + Number(this.assetsPending.texture);
  }

  protected onAssetPendingChange(e: { kind: "swatch" | "texture"; pending: boolean }): void {
    this.assetsPending = { ...this.assetsPending, [e.kind]: e.pending };
  }

  /**
   * Create the fabric, then upload whatever images were staged against it.
   *
   * The order is forced by the API, not chosen: the upload endpoints are
   * POST /fabrics/:id/swatch|texture, so there is nothing to upload *to* until
   * the document exists and has an _id. That is why this form used to hand the
   * user off to the editor to do the images as a second, manual step — the images
   * were staged on a fabric that didn't exist yet.
   *
   * The two halves are separate writes against separate stores (Mongo, then
   * Wasabi) and cannot share a transaction, so a failed upload does NOT undo the
   * fabric. Deleting it to "clean up" would throw away metadata the user just
   * typed in order to report a problem that only affects the images. Instead the
   * fabric stands, the user is told plainly which half failed, and they land in
   * the editor where the staged image can be re-picked and retried.
   */
  protected async add(): Promise<void> {
    // The button is disabled while invalid, but a stray Enter keypress in a text
    // input still submits the form — so the guard has to live here too.
    if (this.saving) return;
    if (!this.isValid) return this.notifyInvalid();
    this.saving = true;

    this.fabric.color_group = [...this.colorGroupModel];
    this.fabric.pattern_group = [...this.patternGroupModel];
    if (this.fabric.brand.brand_collection && !this.fabric.brand.brand_collection.name) {
      delete this.fabric.brand.brand_collection;
    }

    // Read BEFORE committing: commit() clears each block's staged state, and the
    // resulting pending-change events land asynchronously.
    const hadAssets = this.pendingAssetCount > 0;
    let created: IFabric;
    try {
      created = await this.fabricService.create(this.fabric);
    } catch (err: any) {
      this.saving = false;
      UIkit.notification({
        message: err?.response?.data?.message || "There was a problem adding this fabric.",
        status: "danger",
      });
      return;
    }

    const code = created?.mfr_code || this.fabric.mfr_code;
    const group = created?.name?.group || this.fabric.name.group;

    let assetError = "";
    if (hadAssets) {
      try {
        await this.commitAssets(created._id || "");
      } catch (err: any) {
        assetError =
          err?.response?.data?.message ||
          "the images could not be uploaded. Re-attach them in the editor.";
      }
    }

    this.saving = false;

    // The fabric exists either way, so the toast leads with that and then tells
    // the truth about the images rather than implying the whole save failed.
    if (assetError) {
      UIkit.notification({
        message: `${code} was created, but ${assetError}`,
        status: "warning",
        timeout: 8000,
      });
    } else {
      const imagePart = hadAssets ? " Images uploaded." : "";
      UIkit.notification({
        message: `${code} added to the catalog.${imagePart}`,
        status: "success",
      });
    }

    // On to the editor either way: it's where a failed image gets retried, and
    // where the rest of the fabric gets refined. getByMfrCode re-resolves the
    // saved doc, so the editor shows what the server actually stored.
    this.$router
      .push({ name: "edit", query: { code, ...(group ? { group } : {}) } })
      .catch(() => undefined);
  }

  /**
   * Flush the staged asset blocks against the freshly-minted _id.
   *
   * Swatch before texture, and awaited in sequence rather than in parallel: a
   * swatch upload can seed the texture ("Also seed the 3D texture from this
   * swatch"), so a texture uploaded alongside it could be overwritten by the
   * swatch's copy depending on which request landed last. In order, the explicit
   * texture wins — which is what the user asked for by attaching one.
   *
   * Throws on the first failure so add() can report it.
   */
  private async commitAssets(id: string): Promise<void> {
    if (!id) return;
    // The upload blocks read the id off their `fabric-id` prop, so it has to
    // reach them before commit() is called — until it does, commit() is a no-op.
    this.createdId = id;
    await this.$nextTick();

    for (const block of [this.$refs.swatchUpload, this.$refs.textureUpload]) {
      const res = await block?.commit();
      if (!res) continue;
      // Keep the local doc in step so a retry in this form (or the preview) is
      // pointed at the key the server actually wrote.
      if (res.texture_key) this.fabric.texture_key = res.texture_key;
      if (res.kind === "swatch" && res.image) this.fabric.image = res.image;
    }
  }
}
